import { zodResolver } from "@hookform/resolvers/zod";
import { Resolver, SubmitHandler, useForm } from "react-hook-form";
import { TSiteVisitSchema, SiteVisitSchema, SiteVisitDefaultValues } from "../Models";
import { Button } from "@/components/ui/button";
import { Dispatch, JSX, SetStateAction, useState } from "react";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "react-toastify";
import { TenderDurationFragment } from "@/graphql/__generated__/graphql";
import { useTranslation } from "react-i18next";
import { CustomToastComponent } from "@/components/shared";
import SiteVisitFormInputs from "./Shared/SiteVisitFormInputs";
import { SubmitAction } from "@/constants/enums";
import { CircleLoader } from "@/components/shared/loaders";

interface Props {
  onOpen: boolean;
  serviceID: number | undefined;
  onAddSiteVisit: (_siteVisit: TSiteVisitSchema, _action: SubmitAction, _setShowSiteVisitsModal: Dispatch<SetStateAction<boolean>>) => Promise<void>;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  tenderDuration: TenderDurationFragment;
  isProcessing?: boolean;
};

/**
 * Renders the form for adding a site visit.
 * Wrapped in a Dialog component.
 *
 * @param {Props} props - The component props.
 * @returns {JSX.Element} The rendered component.
 */
const AddSiteVisitComponent = (props: Props): JSX.Element => {
  const { onOpen, onOpenChange, onAddSiteVisit, tenderDuration, isProcessing } = props;

  const form = useForm<TSiteVisitSchema>({
    resolver: zodResolver(SiteVisitSchema) as Resolver<TSiteVisitSchema>,
    mode: "onChange",
    defaultValues: SiteVisitDefaultValues,
  });

  const { t } = useTranslation(["modals", "common"]);

  // Used as dynamic key for select to force re-render of the Select component
  const [resetType, setResetType] = useState<boolean>(false);

  const onInvalid = (errors: unknown) => console.error(errors);

  /**
   * OnSubmit handler for the form.
   * @param {TSiteVisitSchema} data
   */
  const onSubmit: SubmitHandler<TSiteVisitSchema> = async (data: TSiteVisitSchema, event?: React.BaseSyntheticEvent) => {
    if (!event) {
      console.error("No event provided to onSubmit handler.");
      return;
    }

    try {
      // Safely access the submitter
      const nativeEvent = event.nativeEvent as SubmitEvent;
      const submitter = nativeEvent.submitter;

      if (!(submitter instanceof HTMLButtonElement)) {
        console.warn("Submitter is not an HTMLButtonElement or is undefined.");
        return;
      }

      const action = submitter.name as SubmitAction;

      if (!Object.values(SubmitAction).includes(action)) {
        console.warn(`Unexpected submit action: ${action}`);
        return;
      }

      form.clearErrors();

      // Call parent function to add site visit
      await onAddSiteVisit(data, action, onOpenChange);

      setResetType((prev) => !prev);

      // Reset form and show success toast only on success
      form.reset(SiteVisitDefaultValues);
      toast(CustomToastComponent, {
        toastId: "site-visit-added-success",
        data: {
          title: t("addsitevisit.toasts.addedsuccessfully"),
          type: "success",
        }
      });
    }
    catch (error) {
      toast(CustomToastComponent, {
        toastId: "site-visit-added-error",
        data: {
          title: t("toasts:toasts.somethingwentwrong"),
          content: error.message,
          type: "error",
        }
      });
    }
  };

  return (
    <Dialog open={onOpen} onOpenChange={onOpenChange}>
      <DialogContent className="select-none overflow-y-auto max-w-[90vw] lg:max-w-fit max-h-[90vh] h-full md:h-auto">
        <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="space-y-6" id="add-site-visit-form">
          <DialogHeader className="text-left">
            <DialogTitle>{t("addsitevisit.title")}</DialogTitle>
            <DialogDescription>
              {t("addsitevisit.description")}
            </DialogDescription>
          </DialogHeader>

          <SiteVisitFormInputs
            form={form}
            tenderDuration={tenderDuration}
            resetType={resetType}
          />

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">
                {t("common:buttons.close")}
              </Button>
            </DialogClose>
            <Button variant="default" type="submit" name={SubmitAction.Add} disabled={isProcessing} className="gap-2">
              <CircleLoader visible={isProcessing} height={16} width={16} strokeWidth={2} />
              {t("common:buttons.add")}
            </Button>
            <Button variant="default" type="submit" name={SubmitAction.AddAndClose} disabled={isProcessing} className="gap-2">
              <CircleLoader visible={isProcessing} height={16} width={16} strokeWidth={2} />
              {t("common:buttons.addandclose")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddSiteVisitComponent;
